import { ButtonContainer, Buttons, ButtonsOptionsConfig } from "../../../generic-sprites/Buttons"
import { PhaserText, PhaserTextConfig } from "../../../utils/PhaserText"

export class TextBox {

    static create = (
        game: Phaser.Game, group: Phaser.Group, config: RegularTextBoxConfig, audioKey?: string, moveUp?: boolean, fade?: boolean, onStop?: () => void) => {
        const graphic = TextBox.createWhiteBox(game, group, config)
        const textArray = config.texts.map(textConfig => PhaserText.create(game, textConfig.text, textConfig.config))

        if (audioKey) {
            const audio = game.add.audio(audioKey)
            if (onStop !== undefined) {
                audio.onStop.addOnce(onStop)
            }
            audio.play()
        }

        if (moveUp) {
            const velocity = 200
            game.physics.enable(group, Phaser.Physics.ARCADE)
            graphic.body.velocity.y -= velocity
            textArray.forEach(text => text.body.velocity.y -= velocity)
        }

        if (fade) {
            const fadeTween = game.add.tween(group).to({alpha: 0}, 500, Phaser.Easing.Linear.None)
            fadeTween.onComplete.addOnce(() => group.destroy(true))
            fadeTween.start()
        }
    }

    static createWithBulletedText = (
        game: Phaser.Game, audioKey: string, config: BulletedTextBoxConfig, onStart: () => void = undefined, onStop: () => void = undefined) => {
        const textGroup = game.add.group()
        TextBox.createWhiteBox(game, textGroup, config)

        const timer = game.time.create(true)
        const bulletCreator = PhaserText.bulletList(game, {x: config.x + 20, y: config.y + 20, fontPx: 18, wordWrapWidth: 0.5, group: textGroup})

        const audio = game.add.audio(audioKey)
        audio.onPlay.addOnce(() => {
            timer.start()
            if (onStart !== undefined) {
                onStart()
            }
        })
        audio.onStop.addOnce(() => {
            textGroup.destroy(true)
            if (onStop !== undefined) {
                onStop()
            }
        })
        audio.play()

        config.texts.forEach((text: TextBoxText) => {
            timer.add(text.delay, () => bulletCreator(text.text, false))
        })
    }

    static createWithButtons = (
        game: Phaser.Game, group: Phaser.Group, config: ButtonTextBoxConfig, audioKey?: string, onStart?: () => void, onStop?: () => void
        ): Array<ButtonContainer> => {
        TextBox.createWhiteBox(game, group, config)
        PhaserText.create(game, config.textConfig.text, config.textConfig.config)
        const buttonArray = config.buttonConfigs.map((button: ButtonConfig) => Buttons.create(game, button.text, button.onClick, button.config))
        const filtered = buttonArray.filter(button => button.button.inputEnabled)
        if (audioKey) {
            const audio = game.add.audio(audioKey)
            audio.onPlay.addOnce(() => {
                filtered.forEach(button => button.disable())
                if (onStart !== undefined) {
                    onStart()
                }
            })
            audio.onStop.addOnce(() => {
                filtered.forEach(button => button.enable())
                if (onStop !== undefined) {
                    onStop()
                }
            })
            audio.play()
        }
        return filtered
    }

    private static createWhiteBox = (game: Phaser.Game, group: Phaser.Group, config: TextBoxConfig): Phaser.Graphics => {
        const graphic = game.add.graphics()
        group.add(graphic)
        graphic.beginFill(Phaser.Color.WHITE, 0.9)
        graphic.lineStyle(6, Phaser.Color.BLACK, 1)
        graphic.drawRoundedRect(config.x, config.y, config.width, config.height, 10)
        graphic.endFill()
        return graphic
    }

}

export interface TextBoxConfig {
    x: number
    y: number
    width: number
    height: number
}

export interface RegularTextBoxConfig extends TextBoxConfig {
    texts: Array<TextConfig>
}

export interface BulletedTextBoxConfig extends TextBoxConfig {
    texts: Array<TextBoxText>
}

export interface ButtonTextBoxConfig extends TextBoxConfig {
    textConfig: TextConfig
    buttonConfigs: Array<ButtonConfig>
}

export interface ButtonConfig {
    text: string
    onClick: () => void
    config: ButtonsOptionsConfig
}

export interface TextConfig {
    text: string
    config: PhaserTextConfig
}

export class TextBoxText {
    text: string
    delay: number

    constructor(text: string, delay: number) {
        this.text = text
        this.delay = delay
    }
}